export interface HoldEvent {
  /** Début de l'activation. */
  readonly t: number;
  /** Valeur maximale atteinte avant confirmation. */
  readonly peak: number;
}

export interface HoldDetectorOptions {
  /** Au-dessus : activation candidate. */
  readonly on: number;
  /** En dessous : fin d'activation (hystérésis, `off < on`). */
  readonly off: number;
  /** Durée continue d'activation avant confirmation. */
  readonly minDurationMs: number;
  /** Trou maximal sans mesure toléré au sein d'une activation. */
  readonly maxGapMs: number;
}

export interface HoldDetector {
  /** `value === null` (ou non finie) : pas de mesure sur cette frame. */
  push(t: number, value: number | null): HoldEvent | null;
  reset(): void;
}

type State =
  | { readonly mode: 'idle' }
  | { readonly mode: 'rising'; readonly onset: number; readonly peak: number }
  | { readonly mode: 'fired' };

/**
 * Signal horodaté → événement quand il reste « haut » assez longtemps.
 * Un seul événement par activation ; réarmé quand le signal repasse sous `off`
 * ou disparaît plus de `maxGapMs`. Frames dupliquées / dans le désordre ignorées.
 */
export function createHoldDetector(options: HoldDetectorOptions): HoldDetector {
  const { on, off, minDurationMs, maxGapMs } = options;
  if (!(off < on)) throw new Error('off doit être < on');
  if (minDurationMs < 0 || maxGapMs < 0) throw new Error('durées négatives');

  let state: State = { mode: 'idle' };
  let lastT = -Infinity;
  let lastValueT = -Infinity;

  return {
    push(t, value) {
      if (t <= lastT) return null;
      lastT = t;
      if (state.mode !== 'idle' && t - lastValueT > maxGapMs) state = { mode: 'idle' };
      if (value === null || !Number.isFinite(value)) return null;
      lastValueT = t;

      if (state.mode === 'idle') {
        if (value >= on) state = { mode: 'rising', onset: t, peak: value };
      } else if (value < off) {
        state = { mode: 'idle' };
      } else if (state.mode === 'rising') {
        state = { ...state, peak: Math.max(state.peak, value) };
      }

      if (state.mode === 'rising' && t - state.onset >= minDurationMs) {
        const event = { t: state.onset, peak: state.peak };
        state = { mode: 'fired' };
        return event;
      }
      return null;
    },
    reset() {
      state = { mode: 'idle' };
      lastT = -Infinity;
      lastValueT = -Infinity;
    },
  };
}
